import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Form, Input, Radio, Checkbox } from 'antd';
import '../Modal/DiscountConfigModal.css';
import {
  MIN_PURCHASE_REQUIREMENTS,
  MIN_PURCHASE_REQUIREMENTS_API_TYPES,
  MAX_DISCOUNT_USES,
  MAX_DISCOUNT_USES_API_TYPES,
  END_DATE_UNIT,
  FORM_FIELDS,
  DEFAULT_VALUES,
  VALIDATION_MESSAGES,
  UI_TEXT,
  PLACEHOLDERS,
  INPUT_ATTRIBUTES,
  RESTRICTED_KEYS
} from '../Modal/discountConfigConstants';
import logger from 'utils/logger';

/**
 * DiscountConfigForm Component
 *
 * Reusable form component for configuring discount options including:
 * - Minimum purchase requirements (amount, quantity, or none)
 * - Maximum discount uses (limited or unlimited)
 * - Validity period (number of days)
 *
 * Can be used both in a modal and inline in edit forms.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} [props.form] - Ant Design form instance (optional, will create one if not provided)
 * @param {Object} [props.discountConfig={}] - Current discount configuration from backend API
 * @param {boolean} [props.isOnboarding=false] - Whether this is used in onboarding flow (affects default values)
 * @param {boolean} [props.inline=false] - Whether this form is displayed inline (affects styling)
 * @param {Function} [props.onChange] - Callback function called when form values change, receives transformed payload
 * @returns {JSX.Element} The discount configuration form component
 */
const DiscountConfigForm = forwardRef(({
  form: externalForm,
  discountConfig = {},
  isOnboarding = false,
  inline = false,
  onChange,
  disabled = false
}, ref) => {
  // Use external form if provided, otherwise create internal form
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;

  /**
   * Memoized default values for the form based on onboarding state.
   * Returns different defaults for onboarding vs regular flow.
   *
   * @returns {Object} Default form values with keys matching FORM_FIELDS constants
   */
  const defaultValues = React.useMemo(() => {
    if (isOnboarding) {
      return DEFAULT_VALUES.ONBOARDING;
    }
    return DEFAULT_VALUES.REGULAR;
  }, [isOnboarding]);

  // Watch form field values to conditionally render dependent fields
  const minPurchaseRequirements = Form.useWatch(FORM_FIELDS.MIN_PURCHASE_REQUIREMENTS, form);
  const maxDiscountUses = Form.useWatch(FORM_FIELDS.MAX_DISCOUNT_USES, form);
  // Track if form has been initialized to prevent re-initialization during editing
  const initializedRef = useRef(false);

  const initialFormRestTime = 100;
  const delayedFormRestTime = 50;


  /**
   * Validates minimum purchase amount input.
   * Ensures value is a number and not negative.
   *
   * @param {Object} _ - Form field object (unused)
   * @param {string} value - Input value to validate
   * @returns {Promise} Resolves if valid, rejects with error message if invalid
   */
  const validateMinPurchaseAmount = (_, value) => {
    // Skip validation if value is empty - required rule handles that
    if (!value || value === '') {
      return Promise.resolve();
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return Promise.reject(new Error(VALIDATION_MESSAGES.MIN_PURCHASE_AMOUNT_NUMBER));
    }
    if (numValue < 0) {
      return Promise.reject(new Error(VALIDATION_MESSAGES.MIN_PURCHASE_AMOUNT_MIN));
    }
    return Promise.resolve();
  };

  /**
   * Validates minimum purchase quantity input.
   * Ensures value is a positive integer.
   *
   * @param {Object} _ - Form field object (unused)
   * @param {string} value - Input value to validate
   * @returns {Promise} Resolves if valid, rejects with error message if invalid
   */
  const validateMinPurchaseQuantity = (_, value) => {
    // Skip validation if value is empty - required rule handles that
    if (!value || value === '') {
      return Promise.resolve();
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return Promise.reject(new Error(VALIDATION_MESSAGES.MIN_PURCHASE_AMOUNT_NUMBER));
    }
    if (!Number.isInteger(numValue)) {
      return Promise.reject(new Error(VALIDATION_MESSAGES.MIN_PURCHASE_QUANTITY_INTEGER));
    }
    if (numValue < 1) {
      return Promise.reject(new Error(VALIDATION_MESSAGES.MIN_PURCHASE_QUANTITY_MIN));
    }
    return Promise.resolve();
  };

  /**
   * Validates maximum discount uses count input.
   * Ensures value is a positive integer.
   *
   * @param {Object} _ - Form field object (unused)
   * @param {string} value - Input value to validate
   * @returns {Promise} Resolves if valid, rejects with error message if invalid
   */
  const validateMaxDiscountUsesCount = (_, value) => {
    // Skip validation if value is empty - required rule handles that
    if (!value || value === '') {
      return Promise.resolve();
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return Promise.reject(new Error(VALIDATION_MESSAGES.MAX_DISCOUNT_USES_COUNT_NUMBER));
    }
    if (!Number.isInteger(numValue)) {
      return Promise.reject(new Error(VALIDATION_MESSAGES.MAX_DISCOUNT_USES_COUNT_INTEGER));
    }
    if (numValue < 1) {
      return Promise.reject(new Error(VALIDATION_MESSAGES.MAX_DISCOUNT_USES_COUNT_MIN));
    }
    return Promise.resolve();
  };

  /**
   * Validates end date input.
   * Ensures value is a positive integer if provided, or allows empty value.
   *
   * @param {Object} _ - Form field object (unused)
   * @param {string} value - Input value to validate
   * @returns {Promise} Resolves if valid, rejects with error message if invalid
   */
  const validateEndDate = (_, value) => {
    if (value === '' || value === null || value === undefined) {
      return Promise.resolve();
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return Promise.reject(new Error(VALIDATION_MESSAGES.END_DATE_NUMBER));
    }
    if (!Number.isInteger(numValue) || numValue <= 0) {
      return Promise.reject(
        new Error(VALIDATION_MESSAGES.END_DATE_POSITIVE_INTEGER)
      );
    }
    return Promise.resolve();
  };

  /**
   * Prevents wheel scroll from changing number input values.
   * Blurs the input when wheel event occurs to prevent accidental changes.
   *
   * @param {Event} e - Wheel event object
   * @returns {void}
   */
  const handleWheelBlur = e => {
    // Prevent scroll from changing number input value
    e.target.blur();
  };

  /**
   * Checks if a key is a restricted key for number inputs.
   * Returns true if the key should be prevented.
   *
   * @param {string} key - The key to check
   * @returns {boolean} True if key should be prevented
   */
  const isRestrictedKey = key => {
    const restrictedKeys = [
      RESTRICTED_KEYS.NEGATIVE,
      RESTRICTED_KEYS.DECIMAL,
      RESTRICTED_KEYS.SCIENTIFIC_E,
      RESTRICTED_KEYS.SCIENTIFIC_E_UPPER,
      RESTRICTED_KEYS.PLUS
    ];
    return restrictedKeys.includes(key);
  };

  /**
   * Prevents invalid key presses in number inputs.
   * Blocks negative sign, decimal point, scientific notation, and plus sign.
   *
   * @param {Event} e - Keyboard event object
   * @returns {void}
   */
  const handleKeyDown = e => {
    // Prevent negative sign, decimal point, and 'e' (scientific notation)
    if (isRestrictedKey(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Parses minimum purchase requirements from API format to form values.
   * Handles three types: amount, quantity, or no requirements.
   *
   * @param {Object} apiConfig - API configuration object
   * @param {Object} defaults - Default form values
   * @returns {Object} Object containing minPurchaseType, minPurchaseAmount, minPurchaseQuantity
   */
  const parseMinPurchaseRequirements = React.useCallback((apiConfig, defaults) => {
    let minPurchaseType = defaults.min_purchase_requirements;
    let minPurchaseAmount = defaults.min_purchase_amount;
    let minPurchaseQuantity = defaults.min_purchase_quantity;

    if (apiConfig.min_purchase_requirements) {
      const type = apiConfig.min_purchase_requirements.TYPE;
      if (type === MIN_PURCHASE_REQUIREMENTS_API_TYPES.MIN_PURCHASE_AMOUNT) {
        minPurchaseType = MIN_PURCHASE_REQUIREMENTS.AMOUNT;
        if (apiConfig.min_purchase_requirements.VALUE) {
          minPurchaseAmount = apiConfig.min_purchase_requirements.VALUE.toString();
        } else {
          minPurchaseAmount = '';
        }
      } else if (type === MIN_PURCHASE_REQUIREMENTS_API_TYPES.MIN_ITEMS_QUANTITY) {
        minPurchaseType = MIN_PURCHASE_REQUIREMENTS.QUANTITY;
        if (apiConfig.min_purchase_requirements.VALUE) {
          minPurchaseQuantity = apiConfig.min_purchase_requirements.VALUE.toString();
        } else {
          minPurchaseQuantity = '';
        }
      } else {
        minPurchaseType = MIN_PURCHASE_REQUIREMENTS.NO_REQUIREMENTS;
      }
    }

    return { minPurchaseType, minPurchaseAmount, minPurchaseQuantity };
  }, []);

  /**
   * Parses maximum discount uses from API format to form values.
   * Handles total usage limits and per-customer restrictions.
   *
   * @param {Object} apiConfig - API configuration object
   * @param {Object} defaults - Default form values
   * @returns {Object} Object containing maxDiscountUsesValue, maxDiscountUsesCount, maxDiscountUsesPerCustomer
   */
  const parseMaxDiscountUses = React.useCallback((apiConfig, defaults) => {
    let maxDiscountUsesValue = defaults.max_discount_uses;
    let maxDiscountUsesCount = defaults.max_discount_uses_count;
    let maxDiscountUsesPerCustomer = defaults.max_discount_uses_per_customer;

    if (apiConfig.max_discount_uses && Array.isArray(apiConfig.max_discount_uses)) {
      const totalUsage = apiConfig.max_discount_uses.find(
        item => item.TYPE === MAX_DISCOUNT_USES_API_TYPES.TOTAL_DISCOUNT_USAGE
      );
      const singleUse = apiConfig.max_discount_uses.find(
        item => item.TYPE === MAX_DISCOUNT_USES_API_TYPES.SINGLE_USE_PER_CUSTOMER
      );

      if (totalUsage && totalUsage.CHECKED) {
        maxDiscountUsesValue = MAX_DISCOUNT_USES.LIMITED;
        if (totalUsage.VALUE) {
          maxDiscountUsesCount = totalUsage.VALUE.toString();
        } else {
          maxDiscountUsesCount = defaults.max_discount_uses_count;
        }
      }

      if (singleUse) {
        maxDiscountUsesPerCustomer = singleUse.CHECKED || false;
      }
    }

    return { maxDiscountUsesValue, maxDiscountUsesCount, maxDiscountUsesPerCustomer };
  }, []);

  /**
   * Parses end date from API format to form value.
   * Converts API value to string representation for form input.
   *
   * @param {Object} apiConfig - API configuration object
   * @param {Object} defaults - Default form values
   * @returns {string} End date value as string
   */
  const parseEndDate = React.useCallback((apiConfig, defaults) => {
    let endDate = defaults.end_date;
    if (apiConfig.end_date && apiConfig.end_date.VALUE) {
      // Convert to days if needed (assuming UNIT is 'days' for now)
      endDate = apiConfig.end_date.VALUE.toString();
    }
    return endDate;
  }, []);

  /**
   * Parses discountConfig from API format to form values format.
   * Transforms nested API structure into flat form field values.
   *
   * @returns {Object} Parsed form values object with keys matching FORM_FIELDS constants.
   * Contains: min_purchase_requirements (string), min_purchase_amount (string),
   * min_purchase_quantity (string), max_discount_uses (string), max_discount_uses_count (string),
   * max_discount_uses_per_customer (boolean), end_date (string)
   */
  const getInitialValues = React.useCallback(() => {
    // If no config or empty config, return defaults
    // In onboarding, this will be onboarding defaults; otherwise regular defaults
    if (!discountConfig || Object.keys(discountConfig).length === 0) {
      return { ...defaultValues };
    }

    // Parse API config if it exists
    // Parse min_purchase_requirements from API format
    const { minPurchaseType, minPurchaseAmount, minPurchaseQuantity } =
      parseMinPurchaseRequirements(discountConfig, defaultValues);

    // Parse max_discount_uses from API format
    const { maxDiscountUsesValue, maxDiscountUsesCount, maxDiscountUsesPerCustomer } =
      parseMaxDiscountUses(discountConfig, defaultValues);

    // Parse end_date from API format
    const endDate = parseEndDate(discountConfig, defaultValues);

    return {
      [FORM_FIELDS.MIN_PURCHASE_REQUIREMENTS]: minPurchaseType,
      [FORM_FIELDS.MIN_PURCHASE_AMOUNT]: minPurchaseAmount,
      [FORM_FIELDS.MIN_PURCHASE_QUANTITY]: minPurchaseQuantity,
      [FORM_FIELDS.MAX_DISCOUNT_USES]: maxDiscountUsesValue,
      [FORM_FIELDS.MAX_DISCOUNT_USES_COUNT]: maxDiscountUsesCount,
      [FORM_FIELDS.MAX_DISCOUNT_USES_PER_CUSTOMER]: maxDiscountUsesPerCustomer,
      [FORM_FIELDS.END_DATE]: endDate
    };
  }, [discountConfig, defaultValues, parseMinPurchaseRequirements, parseMaxDiscountUses, parseEndDate]);

  // Calculate initial values for the form - use defaults if no config in onboarding
  const formInitialValues = React.useMemo(() => {
    const hasConfig = discountConfig && Object.keys(discountConfig).length > 0;
    if (!hasConfig && isOnboarding) {
      // In onboarding with no config, use onboarding defaults
      return { ...defaultValues };
    }
    return getInitialValues();
  }, [discountConfig, isOnboarding, defaultValues, getInitialValues]);

  /**
   * Effect hook to initialize form values when discountConfig changes.
   * Only initializes once to prevent resetting user input.
   * Uses defaults only in onboarding flow when no config exists.
   *
   * @effect
   * @dependencies {Object} discountConfig - Discount configuration from API
   * @dependencies {boolean} isOnboarding - Whether this is onboarding flow
   */
  // Update maxDiscountUsesValue when maxDiscountUses changes
  React.useEffect(() => {
    if (maxDiscountUses) {
      logger.info('Form field MAX_DISCOUNT_USES changed to:', maxDiscountUses);
    }
  }, [maxDiscountUses]);

  useEffect(() => {
    const hasConfig = discountConfig && Object.keys(discountConfig).length > 0;

    if (!initializedRef.current) {
      // Initialize form with values
      const initialValues = getInitialValues();
      // Log for debugging in onboarding
      if (isOnboarding && !hasConfig) {
        logger.info('Initializing form with onboarding defaults:', initialValues);
        logger.info('Form field names:', Object.keys(initialValues));
      }
      // Use multiple attempts to ensure form is ready
      const initForm = () => {
        try {
          const currentValues = form.getFieldsValue();
          logger.info('Current form values before init:', currentValues);
          // Only set if form is empty or values differ
          const needsUpdate = Object.keys(currentValues).length === 0 ||
            JSON.stringify(currentValues) !== JSON.stringify(initialValues);
          if (needsUpdate) {
            logger.info('Setting form values to:', initialValues);
            form.setFieldsValue(initialValues);
            const newValues = form.getFieldsValue();
            logger.info('Form values after setFieldsValue:', newValues);
          }
          initializedRef.current = true;
        } catch (error) {
          logger.error('Error setting form values:', error);
          // Retry after a short delay
          setTimeout(() => {
            try {
              logger.info('Retrying form initialization with:', initialValues);
              form.setFieldsValue(initialValues);
              initializedRef.current = true;
            } catch (retryError) {
              logger.error('Error retrying form initialization:', retryError);
            }
          }, initialFormRestTime);
        }
      };
      // Try immediately, then with a small delay to ensure form is mounted
      setTimeout(initForm, 0);
      setTimeout(initForm, delayedFormRestTime);
    } else if (isOnboarding && !hasConfig) {
      // If already initialized but in onboarding with no config, ensure defaults are set
      // This handles the case where discountConfig changes from {} to null
      logger.info('Re-setting form to defaults:', defaultValues);
      form.setFieldsValue(defaultValues);
    } else {
      logger.info('Form already initialized, skipping initialization');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountConfig, isOnboarding]);

  /**
   * Transforms form values to API format.
   * Converts flat form values into nested API structure expected by backend.
   *
   * @param {Object} values - Form values from Ant Design Form
   * @returns {Object} API-formatted discount configuration
   */
  const transformFormValuesToApiFormat = React.useCallback(values => {
    const parseMinPurchaseAmountValue = () => {
      if (values[FORM_FIELDS.MIN_PURCHASE_AMOUNT]) {
        return parseFloat(values[FORM_FIELDS.MIN_PURCHASE_AMOUNT]);
      }
      return null;
    };

    const parseMinPurchaseQuantityValue = () => {
      if (values[FORM_FIELDS.MIN_PURCHASE_QUANTITY]) {
        return parseInt(values[FORM_FIELDS.MIN_PURCHASE_QUANTITY], 10);
      }
      return null;
    };

    const parseMaxDiscountUsesCountValue = () => {
      if (values[FORM_FIELDS.MAX_DISCOUNT_USES_COUNT]) {
        return parseInt(values[FORM_FIELDS.MAX_DISCOUNT_USES_COUNT], 10);
      }
      return null;
    };

    const transformMinPurchaseRequirements = () => {
      if (values[FORM_FIELDS.MIN_PURCHASE_REQUIREMENTS] === MIN_PURCHASE_REQUIREMENTS.AMOUNT) {
        return {
          TYPE: MIN_PURCHASE_REQUIREMENTS_API_TYPES.MIN_PURCHASE_AMOUNT,
          VALUE: parseMinPurchaseAmountValue()
        };
      }
      if (values[FORM_FIELDS.MIN_PURCHASE_REQUIREMENTS] === MIN_PURCHASE_REQUIREMENTS.QUANTITY) {
        return {
          TYPE: MIN_PURCHASE_REQUIREMENTS_API_TYPES.MIN_ITEMS_QUANTITY,
          VALUE: parseMinPurchaseQuantityValue()
        };
      }
      return {
        TYPE: MIN_PURCHASE_REQUIREMENTS_API_TYPES.NO_MIN_REQUIREMENTS,
        VALUE: null
      };
    };

    const transformMaxDiscountUses = () => {
      const maxDiscountUsesArray = [];

      // Add total_discount_usage item
      if (values[FORM_FIELDS.MAX_DISCOUNT_USES] === MAX_DISCOUNT_USES.LIMITED) {
        maxDiscountUsesArray.push({
          TYPE: MAX_DISCOUNT_USES_API_TYPES.TOTAL_DISCOUNT_USAGE,
          CHECKED: true,
          VALUE: parseMaxDiscountUsesCountValue()
        });
      } else {
        maxDiscountUsesArray.push({
          TYPE: MAX_DISCOUNT_USES_API_TYPES.TOTAL_DISCOUNT_USAGE,
          CHECKED: false,
          VALUE: null
        });
      }

      // Add single_use_per_customer item
      maxDiscountUsesArray.push({
        TYPE: MAX_DISCOUNT_USES_API_TYPES.SINGLE_USE_PER_CUSTOMER,
        CHECKED: values[FORM_FIELDS.MAX_DISCOUNT_USES_PER_CUSTOMER] || false,
        VALUE: null
      });

      return maxDiscountUsesArray;
    };

    const transformEndDate = () => {
      if (values[FORM_FIELDS.END_DATE] && values[FORM_FIELDS.END_DATE] !== '') {
        return {
          VALUE: parseInt(values[FORM_FIELDS.END_DATE], 10),
          UNIT: END_DATE_UNIT.DAYS
        };
      }
      return null;
    };

    return {
      discount: null,
      discount_type: null,
      min_purchase_requirements: transformMinPurchaseRequirements(),
      max_discount_uses: transformMaxDiscountUses(),
      end_date: transformEndDate()
    };
  }, []);

  /**
   * Renders the minimum purchase requirements form section.
   * Includes radio buttons for requirement type and conditional inputs.
   *
   * @returns {JSX.Element} Minimum purchase requirements form section
   */
  const renderMinPurchaseRequirementsSection = () => (
    <div className='discount-config-section'>
      <h6 className='discount-section-title'>{UI_TEXT.MIN_PURCHASE_REQUIREMENTS_TITLE}</h6>
      <p className='discount-section-description'>
        {UI_TEXT.MIN_PURCHASE_REQUIREMENTS_DESCRIPTION}
      </p>

      {/* Radio group for selecting purchase requirement type */}
      <Form.Item
        name={FORM_FIELDS.MIN_PURCHASE_REQUIREMENTS}
        label={<span className='discount-form-label'>{UI_TEXT.MIN_PURCHASE_TYPE_LABEL}</span>}
        rules={[
          {
            required: true,
            message: VALIDATION_MESSAGES.MIN_PURCHASE_REQUIREMENT_REQUIRED
          }
        ]}
        className='discount-form-group'
      >
        <Radio.Group className='discount-radio-group' disabled={disabled}>
          {/* Option: No minimum requirements */}
          <Radio value={MIN_PURCHASE_REQUIREMENTS.NO_REQUIREMENTS} className='discount-radio-label'>
            <span>{UI_TEXT.NO_MINIMUM_REQUIREMENTS}</span>
          </Radio>
          {/* Option: Minimum purchase amount in dollars */}
          <Radio value={MIN_PURCHASE_REQUIREMENTS.AMOUNT} className='discount-radio-label'>
            <span>{UI_TEXT.MINIMUM_PURCHASE_AMOUNT}</span>
          </Radio>
          {/* Option: Minimum quantity of items */}
          <Radio value={MIN_PURCHASE_REQUIREMENTS.QUANTITY} className='discount-radio-label'>
            <span>{UI_TEXT.MINIMUM_QUANTITY_OF_ITEMS}</span>
          </Radio>
        </Radio.Group>
      </Form.Item>

      {/* Conditionally render amount input when AMOUNT option is selected */}
      {minPurchaseRequirements === MIN_PURCHASE_REQUIREMENTS.AMOUNT && (
        <Form.Item
          name={FORM_FIELDS.MIN_PURCHASE_AMOUNT}
          label={<span className='discount-form-label'>{UI_TEXT.AMOUNT_LABEL}</span>}
          rules={[
            {
              required: true,
              message: VALIDATION_MESSAGES.MIN_PURCHASE_AMOUNT_REQUIRED
            },
            {
              validator: validateMinPurchaseAmount
            }
          ]}
          className='discount-form-group'
        >
          {/* Number input for minimum purchase amount */}
          <Input
            type='number'
            placeholder={PLACEHOLDERS.AMOUNT}
            className='discount-form-control'
            step={INPUT_ATTRIBUTES.AMOUNT_STEP}
            min={INPUT_ATTRIBUTES.AMOUNT_MIN}
            onWheel={handleWheelBlur}
            disabled={disabled}
          />
        </Form.Item>
      )}

      {/* Conditionally render quantity input when QUANTITY option is selected */}
      {minPurchaseRequirements === MIN_PURCHASE_REQUIREMENTS.QUANTITY && (
        <Form.Item
          name={FORM_FIELDS.MIN_PURCHASE_QUANTITY}
          label={<span className='discount-form-label'>{UI_TEXT.MINIMUM_QUANTITY_LABEL}</span>}
          rules={[
            {
              required: true,
              message: VALIDATION_MESSAGES.MIN_PURCHASE_QUANTITY_REQUIRED
            },
            {
              validator: validateMinPurchaseQuantity
            }
          ]}
          className='discount-form-group'
        >
          {/* Number input for minimum purchase quantity */}
          <Input
            type='number'
            placeholder={PLACEHOLDERS.QUANTITY}
            className='discount-form-control'
            min={INPUT_ATTRIBUTES.QUANTITY_MIN}
            onWheel={handleWheelBlur}
            disabled={disabled}
          />
        </Form.Item>
      )}
    </div>
  );

  /**
   * Renders the maximum discount uses form section.
   * Includes radio buttons for usage limit and conditional inputs.
   *
   * @returns {JSX.Element} Maximum discount uses form section
   */
  const renderMaxDiscountUsesSection = () => (
    <div className='discount-config-section'>
      <h6 className='discount-section-title'>{UI_TEXT.MAX_DISCOUNT_USES_TITLE}</h6>
      <p className='discount-section-description'>
        {UI_TEXT.MAX_DISCOUNT_USES_DESCRIPTION}
      </p>

      {/* Radio group for selecting usage limit type */}
      <Form.Item
        name={FORM_FIELDS.MAX_DISCOUNT_USES}
        label={<span className='discount-form-label'>{UI_TEXT.DISCOUNT_USAGE_LIMIT_LABEL}</span>}
        rules={[
          {
            required: true,
            message: VALIDATION_MESSAGES.MAX_DISCOUNT_USES_REQUIRED
          }
        ]}
        className='discount-form-group'
      >
        <Radio.Group
          className='discount-radio-group'
          disabled={disabled}
        >
          {/* Option: Unlimited uses allowed */}
          <Radio value={MAX_DISCOUNT_USES.UNLIMITED} className='discount-radio-label'>
            <span>{UI_TEXT.UNLIMITED_USES}</span>
          </Radio>
          {/* Option: Limited number of uses */}
          <Radio value={MAX_DISCOUNT_USES.LIMITED} className='discount-radio-label'>
            <span>{UI_TEXT.LIMIT_NUMBER_OF_TIMES}</span>
          </Radio>
        </Radio.Group>
      </Form.Item>

      {/* Conditionally render total uses input when LIMITED option is selected */}
      {maxDiscountUses === MAX_DISCOUNT_USES.LIMITED && (
        <Form.Item
          name={FORM_FIELDS.MAX_DISCOUNT_USES_COUNT}
          label={<span className='discount-form-label'>{UI_TEXT.TOTAL_USES_LABEL}</span>}
          rules={[
            {
              required: true,
              message: VALIDATION_MESSAGES.MAX_DISCOUNT_USES_COUNT_REQUIRED
            },
            {
              validator: validateMaxDiscountUsesCount
            }
          ]}
          className='discount-form-group'
        >
          {/* Number input for maximum total uses */}
          <Input
            type='number'
            placeholder={PLACEHOLDERS.TOTAL_USES}
            className='discount-form-control'
            min={INPUT_ATTRIBUTES.TOTAL_USES_MIN}
            onWheel={handleWheelBlur}
            disabled={disabled}
          />
        </Form.Item>
      )}

      {/* Checkbox for limiting to one use per customer */}
      <Form.Item
        name={FORM_FIELDS.MAX_DISCOUNT_USES_PER_CUSTOMER}
        valuePropName='checked'
        className='discount-form-group discount-checkbox-group'
      >
        <Checkbox disabled={disabled}>
          {UI_TEXT.LIMIT_ONE_USE_PER_CUSTOMER}
        </Checkbox>
      </Form.Item>
    </div>
  );

  /**
   * Renders the validity period form section.
   * Includes input for number of days.
   *
   * @returns {JSX.Element} Validity period form section
   */
  const renderValidityPeriodSection = () => (
    <div className='discount-config-section'>
      <h6 className='discount-section-title'>{UI_TEXT.VALIDITY_PERIOD_TITLE}</h6>
      <p className='discount-section-description'>
        {UI_TEXT.VALIDITY_PERIOD_DESCRIPTION}
      </p>

      {/* Number input for validity period in days */}
      <Form.Item
        name={FORM_FIELDS.END_DATE}
        label={<span className='discount-form-label'>{UI_TEXT.NUMBER_OF_DAYS_LABEL}</span>}
        rules={[
          {
            validator: validateEndDate
          }
        ]}
        className='discount-form-group'
      >
        {/* Number input with restrictions to prevent invalid values */}
        <Input
          type='number'
          placeholder={PLACEHOLDERS.END_DATE}
          className='discount-form-control'
          min={INPUT_ATTRIBUTES.END_DATE_MIN}
          step={INPUT_ATTRIBUTES.END_DATE_STEP}
          onWheel={handleWheelBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
      </Form.Item>
    </div>
  );

  /**
   * Handles form value changes and calls onChange callback if provided.
   *
   * @param {Object} changedValues - Changed form values
   * @param {Object} allValues - All form values
   * @returns {void}
   */
  const handleValuesChange = React.useCallback((changedValues, allValues) => {
    if (onChange) {
      try {
        const transformed = transformFormValuesToApiFormat(allValues);
        onChange(transformed);
      } catch (error) {
        logger.error('Error transforming form values:', error);
      }
    }
  }, [onChange, transformFormValuesToApiFormat]);

  // Expose form methods via ref
  useImperativeHandle(ref, () => ({
    form,
    getValues: () => {
      const values = form.getFieldsValue();
      return transformFormValuesToApiFormat(values);
    },
    validate: () => form.validateFields(),
    lastConfig: null
  }), [form, transformFormValuesToApiFormat]);

  return (
    <Form
      form={form}
      layout='vertical'
      className={`discount-config-body ${inline ? 'discount-config-inline' : ''}`}
      onValuesChange={handleValuesChange}
      initialValues={formInitialValues}
    >
      {/* Minimum Purchase Requirements Section */}
      {renderMinPurchaseRequirementsSection()}

      {/* Visual separator between form sections */}
      <hr className='discount-section-divider' />

      {/* Maximum Discount Uses Section */}
      {renderMaxDiscountUsesSection()}

      {/* Visual separator between form sections */}
      <hr className='discount-section-divider' />

      {/* Validity Period (Days) Section */}
      {renderValidityPeriodSection()}
    </Form>
  );
});

DiscountConfigForm.displayName = 'DiscountConfigForm';

export default DiscountConfigForm;

